import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  CANNOT_REVERT_ROOT_REASON,
  COMMIT_NOT_ON_BRANCH_REASON,
  getApiErrorReason,
  NOTHING_TO_REVERT_REASON,
  useRevertCommitMutation,
  useSwitchBranch,
  type Branch,
  type EntityResolution,
  type MergeConflicts,
  type MergeResolutions
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { MergeConflictResolver } from './MergeConflictResolver'

export interface BranchRevertCommitDialogProps {
  branch: Branch
  commit?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

type RevertNotice = { kind: 'error' | 'info'; text: string }

export function BranchRevertCommitDialog({
  branch,
  commit,
  open,
  onOpenChange
}: BranchRevertCommitDialogProps) {
  const [revertCommit, revertState] = useRevertCommitMutation()
  const { switchBranch } = useSwitchBranch()
  const [notice, setNotice] = React.useState<RevertNotice | null>(null)
  const [conflicts, setConflicts] = React.useState<MergeConflicts | null>(null)
  const [choices, setChoices] = React.useState<Record<string, EntityResolution>>({})
  const [lastCommit, setLastCommit] = React.useState<string | undefined>(commit)
  const activeCommit = commit ?? lastCommit
  const short = activeCommit?.slice(0, 8)

  // The resolver is controlled; a fresh conflict set starts from no picks.
  React.useEffect(() => {
    setChoices({})
  }, [conflicts])

  React.useEffect(() => {
    if (commit !== undefined) {
      setLastCommit(commit)
    }
  }, [commit])

  React.useEffect(() => {
    if (!open) {
      setNotice(null)
      setConflicts(null)
      revertState.reset()
    }
  }, [open, revertState])

  const submitting = revertState.isLoading

  const runRevert = async (resolutions?: MergeResolutions) => {
    if (!activeCommit || submitting) return
    setNotice(null)
    try {
      const outcome = await revertCommit({
        branchId: branch.id,
        commit: activeCommit,
        ...(resolutions ? { resolutions } : {})
      }).unwrap()

      if (outcome.status === 'conflicts') {
        setConflicts(outcome.conflicts)
        return
      }

      if (outcome.branch.id !== branch.id) {
        await switchBranch(outcome.branch.id)
        toast.success(`Created ${outcome.branch.name} and switched to it`)
      } else {
        toast.success(
          `Reverted ${short ?? activeCommit} on ${branch.name} — ${outcome.commit.hash.slice(0, 8)}`
        )
      }
      onOpenChange(false)
    } catch (err) {
      const reason = getApiErrorReason(err)
      if (reason === 'HEAD_MOVED') {
        setConflicts(null)
        setNotice({
          kind: 'error',
          text: 'The branch moved while reverting — try again (fresh conflicts may differ).'
        })
        return
      }
      setNotice({
        kind: reason === NOTHING_TO_REVERT_REASON ? 'info' : 'error',
        text: revertError(err)
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {conflicts ? `Resolve revert conflicts — ${short}` : `Revert ${short}?`}
          </DialogTitle>
          <DialogDescription>
            {conflicts
              ? 'Later work touched the same entities this revert would undo. Choose current state or the undone state for each conflict.'
              : branch.protected
                ? 'This creates a new branch from the protected branch, then adds a commit there that undoes this commit. The protected branch is untouched.'
                : 'This adds a new commit that undoes this commit. The original commit and everything after it stay in history.'}
          </DialogDescription>
        </DialogHeader>

        {conflicts ? (
          <MergeConflictResolver
            conflicts={conflicts}
            targetName={branch.name}
            sourceName={`revert of ${short ?? activeCommit ?? 'commit'}`}
            submitLabel="Revert with resolutions"
            submitting={submitting}
            choices={choices}
            onChoicesChange={setChoices}
            onCancel={() => onOpenChange(false)}
            onSubmit={(resolutions) => void runRevert(resolutions)}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {notice ? (
              <p
                role={notice.kind === 'info' ? 'status' : 'alert'}
                className={
                  notice.kind === 'info'
                    ? 'rounded-md bg-st-idle-bg/40 px-3 py-2 text-xs text-ink-3'
                    : 'rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down'
                }
              >
                {notice.text}
              </p>
            ) : null}

            {submitting ? (
              <p className="flex items-center gap-2 text-xs text-ink-3">
                <Loader2 className="size-3 animate-spin" />
                Reverting — this can take a while on large graphs…
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={submitting || !activeCommit}
                onClick={() => void runRevert()}
              >
                {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
                Revert
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function revertError(err: unknown): string {
  switch (getApiErrorReason(err)) {
    case COMMIT_NOT_ON_BRANCH_REASON:
      return "This commit is not on the branch's history."
    case CANNOT_REVERT_ROOT_REASON:
      return 'The root commit cannot be reverted.'
    case NOTHING_TO_REVERT_REASON:
      return 'That commit is already undone on this branch.'
    case 'BRANCH_PROTECTED':
      return 'Could not create a revert branch for this protected branch.'
    case 'BRANCH_NOT_ACTIVE':
      return 'This branch is no longer active.'
    default:
      break
  }
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.length > 0) return message
    }
  }
  return 'Could not revert the commit.'
}
